// ---------------------------------------------------------------------------
// DnD Email Editor – Global Styles Panel
// ---------------------------------------------------------------------------

import type { GlobalStyles, WrapperSettings } from './dnd-model';
import { ColorField } from './ColorField';
import { ColorInput } from './dnd-color-presets';
import { WEB_FONTS } from './dnd-webfonts';

// ---- Reusable inputs (same pattern as inspector) --------------------------

function TextInput({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="dnd-inspector-field">
      <label>{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function NumberInput({ label, value, onChange, suffix, min, max }: { label: string; value: number; onChange: (v: number) => void; suffix?: string; min?: number; max?: number }) {
  return (
    <div className="dnd-inspector-field">
      <label>{label}{suffix && <span className="dnd-suffix">{suffix}</span>}</label>
      <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} min={min} max={max} />
    </div>
  );
}

function SelectInput({ label, value, options, onChange }: { label: string; value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <div className="dnd-inspector-field">
      <label>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function CheckboxInput({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="dnd-inspector-field dnd-inspector-checkbox">
      <label><input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />{label}</label>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return <div className="dnd-inspector-section-header">{title}</div>;
}

const FONT_OPTIONS = [
  // Web fonts first: they are the branded choice, and each carries a web-safe
  // fallback stack for the clients that cannot fetch one (see dnd-webfonts).
  ...WEB_FONTS.map((f) => ({ value: f.stack, label: f.label })),
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, Times, serif', label: 'Georgia' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Courier New, Courier, monospace', label: 'Courier New' },
  { value: 'Times New Roman, Times, serif', label: 'Times New Roman' },
];

// ---- Component ------------------------------------------------------------

interface DndGlobalStylesProps {
  globalStyles: GlobalStyles;
  wrapper: WrapperSettings;
  preheaderText: string;
  onUpdateGlobalStyles: (patch: Partial<GlobalStyles>) => void;
  onUpdateWrapper: (patch: Partial<WrapperSettings>) => void;
  onUpdatePreheader: (text: string) => void;
}

export function DndGlobalStyles({
  globalStyles: gs,
  wrapper,
  preheaderText,
  onUpdateGlobalStyles,
  onUpdateWrapper,
  onUpdatePreheader,
}: DndGlobalStylesProps) {
  return (
    <div className="dnd-inspector">
      <div className="dnd-inspector-title">Design Settings</div>
      <div className="dnd-inspector-scroll">
        <SectionHeader title="Email" />
        <TextInput
          label="Preheader Text"
          value={preheaderText}
          onChange={onUpdatePreheader}
          placeholder="Preview text shown in inbox..."
        />

        <SectionHeader title="Layout" />
        <NumberInput
          label="Content Width"
          value={wrapper.maxWidth}
          onChange={(v) => onUpdateWrapper({ maxWidth: v })}
          suffix="px"
          min={320}
          max={900}
        />
        <ColorField
          label="Body Background"
          variant="background"
          value={gs.bodyBgColor}
          onChange={(v) => onUpdateGlobalStyles({ bodyBgColor: v })}
        />
        <ColorField
          label="Content Background"
          variant="background"
          value={gs.contentBgColor}
          onChange={(v) => onUpdateGlobalStyles({ contentBgColor: v })}
        />
        <ColorField
          label="Wrapper Background"
          variant="background"
          value={wrapper.bgColor}
          onChange={(v) => onUpdateWrapper({ bgColor: v })}
        />

        <SectionHeader title="Typography" />
        <SelectInput
          label="Font Family"
          value={gs.fontFamily}
          options={FONT_OPTIONS}
          onChange={(v) => onUpdateGlobalStyles({ fontFamily: v })}
        />
        <TextInput
          label="Font Size"
          value={gs.fontSize}
          onChange={(v) => onUpdateGlobalStyles({ fontSize: v })}
          placeholder="16px"
        />
        <ColorInput
          label="Text Color"
          value={gs.textColor}
          onChange={(v) => onUpdateGlobalStyles({ textColor: v })}
        />
        <TextInput
          label="Line Height"
          value={gs.lineHeight}
          onChange={(v) => onUpdateGlobalStyles({ lineHeight: v })}
          placeholder="1.5"
        />

        <SectionHeader title="Links" />
        <ColorInput
          label="Link Color"
          value={gs.linkColor}
          onChange={(v) => onUpdateGlobalStyles({ linkColor: v })}
        />
        <CheckboxInput
          label="Underline links"
          value={gs.linkUnderline}
          onChange={(v) => onUpdateGlobalStyles({ linkUnderline: v })}
        />

        <SectionHeader title="Headings" />
        <SelectInput
          label="Heading Font"
          value={gs.headingFontFamily}
          options={FONT_OPTIONS}
          onChange={(v) => onUpdateGlobalStyles({ headingFontFamily: v })}
        />
        <TextInput label="H1 Size" value={gs.h1.fontSize} onChange={(v) => onUpdateGlobalStyles({ h1: { ...gs.h1, fontSize: v } })} />
        <ColorInput label="H1 Color" value={gs.h1.color} onChange={(v) => onUpdateGlobalStyles({ h1: { ...gs.h1, color: v } })} />
        <TextInput label="H2 Size" value={gs.h2.fontSize} onChange={(v) => onUpdateGlobalStyles({ h2: { ...gs.h2, fontSize: v } })} />
        <ColorInput label="H2 Color" value={gs.h2.color} onChange={(v) => onUpdateGlobalStyles({ h2: { ...gs.h2, color: v } })} />
        <TextInput label="H3 Size" value={gs.h3.fontSize} onChange={(v) => onUpdateGlobalStyles({ h3: { ...gs.h3, fontSize: v } })} />
        <ColorInput label="H3 Color" value={gs.h3.color} onChange={(v) => onUpdateGlobalStyles({ h3: { ...gs.h3, color: v } })} />

        <SectionHeader title="Default Button Style" />
        <ColorInput label="Button Color" value={gs.defaultButtonStyle.bgColor} onChange={(v) => onUpdateGlobalStyles({ defaultButtonStyle: { ...gs.defaultButtonStyle, bgColor: v } })} />
        <ColorInput label="Button Text Color" value={gs.defaultButtonStyle.textColor} onChange={(v) => onUpdateGlobalStyles({ defaultButtonStyle: { ...gs.defaultButtonStyle, textColor: v } })} />
        <TextInput label="Border Radius" value={gs.defaultButtonStyle.borderRadius} onChange={(v) => onUpdateGlobalStyles({ defaultButtonStyle: { ...gs.defaultButtonStyle, borderRadius: v } })} />
      </div>
    </div>
  );
}
